import type { LoudnessAnalysis } from './types.js';

interface LoudnessInput {
  samples: Float32Array[];
  sample_rate: number;
}

/**
 * ITU-R BS.1770-4 integrated, short-term, momentary, true-peak, LRA, crest factor.
 *
 * Implementation: K-weighting (high-shelf + RLB high-pass) + 400 ms / 100 ms blocks for
 * momentary, 3 s / 100 ms for short-term; absolute (-70 LUFS) and relative (-10 LU) gates
 * for integrated; 4x oversample peak detection for true peak.
 */
export function analyzeLoudness(input: LoudnessInput): LoudnessAnalysis {
  const sr = input.sample_rate;
  const channels = input.samples.length;
  if (channels === 0) {
    return {
      lufs_integrated: -Infinity,
      lufs_short_term: -Infinity,
      lufs_momentary_max: -Infinity,
      true_peak_dbtp: -Infinity,
      crest_factor_db: 0,
      lra: 0,
    };
  }

  const filtered = input.samples.map((c) => kWeight(c, sr));

  const momentaryWindow = Math.floor(0.4 * sr);
  const hop = Math.floor(0.1 * sr);
  const momentaryLoudness: number[] = [];
  for (let start = 0; start + momentaryWindow <= filtered[0].length; start += hop) {
    const m = meanSquareBlock(filtered, start, momentaryWindow);
    momentaryLoudness.push(m > 0 ? -0.691 + 10 * Math.log10(m) : -Infinity);
  }
  const lufsMomentaryMax =
    momentaryLoudness.length > 0 ? Math.max(...momentaryLoudness) : -Infinity;

  const shortWindow = Math.floor(3 * sr);
  const shortLoudness: number[] = [];
  for (let start = 0; start + shortWindow <= filtered[0].length; start += hop) {
    const m = meanSquareBlock(filtered, start, shortWindow);
    shortLoudness.push(m > 0 ? -0.691 + 10 * Math.log10(m) : -Infinity);
  }
  const lufsShortTerm =
    shortLoudness.length > 0 ? shortLoudness[shortLoudness.length - 1] : -Infinity;

  const absGated = momentaryLoudness.filter((l) => l > -70);
  let integrated = -Infinity;
  if (absGated.length > 0) {
    const meanLin =
      absGated.map((l) => Math.pow(10, (l + 0.691) / 10)).reduce((a, b) => a + b, 0) /
      absGated.length;
    const meanLufs = -0.691 + 10 * Math.log10(meanLin);
    const relThresh = meanLufs - 10;
    const relGated = absGated.filter((l) => l > relThresh);
    if (relGated.length > 0) {
      const finalLin =
        relGated.map((l) => Math.pow(10, (l + 0.691) / 10)).reduce((a, b) => a + b, 0) /
        relGated.length;
      integrated = -0.691 + 10 * Math.log10(finalLin);
    }
  }

  const sortedShort = shortLoudness
    .filter((l) => l > -70 && l > integrated - 20)
    .sort((a, b) => a - b);
  let lra = 0;
  if (sortedShort.length > 1) {
    const lo = sortedShort[Math.floor(0.1 * sortedShort.length)];
    const hi = sortedShort[Math.floor(0.95 * sortedShort.length)];
    lra = hi - lo;
  }

  let truePeak = 0;
  for (const ch of input.samples) {
    for (let i = 1; i < ch.length; i++) {
      const a = ch[i - 1];
      const b = ch[i];
      for (let k = 1; k <= 4; k++) {
        const t = k / 4;
        const v = Math.abs(a + (b - a) * t);
        if (v > truePeak) truePeak = v;
      }
    }
  }
  const truePeakDbtp = truePeak > 0 ? 20 * Math.log10(truePeak) : -Infinity;

  let peakLin = 0;
  let rmsAcc = 0;
  let count = 0;
  for (const ch of input.samples) {
    for (let i = 0; i < ch.length; i++) {
      const a = Math.abs(ch[i]);
      if (a > peakLin) peakLin = a;
      rmsAcc += ch[i] * ch[i];
      count += 1;
    }
  }
  const peakDb = peakLin > 0 ? 20 * Math.log10(peakLin) : -Infinity;
  const rmsLin = count > 0 ? Math.sqrt(rmsAcc / count) : 0;
  const rmsDb = rmsLin > 0 ? 20 * Math.log10(rmsLin) : -Infinity;
  const crestFactor = Number.isFinite(peakDb) && Number.isFinite(rmsDb) ? peakDb - rmsDb : 0;

  return {
    lufs_integrated: integrated,
    lufs_short_term: lufsShortTerm,
    lufs_momentary_max: lufsMomentaryMax,
    true_peak_dbtp: truePeakDbtp,
    crest_factor_db: crestFactor,
    lra,
  };
}

function meanSquareBlock(channels: Float32Array[], start: number, length: number): number {
  let sum = 0;
  for (const ch of channels) {
    let chSum = 0;
    for (let i = start; i < start + length; i++) chSum += ch[i] * ch[i];
    sum += chSum / length;
  }
  return sum;
}

function kWeight(samples: Float32Array, sr: number): Float32Array {
  const out1 = biquad(samples, ...preFilterCoeffs(sr));
  return biquad(out1, ...rlbFilterCoeffs(sr));
}

type BiquadCoeffs = [b0: number, b1: number, b2: number, a1: number, a2: number];

function preFilterCoeffs(sr: number): BiquadCoeffs {
  // Reference 48 kHz BS.1770 high-shelf: ~+3.999843 dB @ 1681.974 Hz, Q ~= 0.7071752.
  const f0 = 1681.974450955533;
  const G = 3.999843853973347;
  const Q = 0.7071752369554196;
  const K = Math.tan((Math.PI * f0) / sr);
  const Vh = Math.pow(10, G / 20);
  const Vb = Math.pow(Vh, 0.4996667741545416);
  const a0 = 1 + K / Q + K * K;
  return [
    (Vh + (Vb * K) / Q + K * K) / a0,
    (2 * (K * K - Vh)) / a0,
    (Vh - (Vb * K) / Q + K * K) / a0,
    (2 * (K * K - 1)) / a0,
    (1 - K / Q + K * K) / a0,
  ];
}

function rlbFilterCoeffs(sr: number): BiquadCoeffs {
  // Reference 48 kHz BS.1770 RLB high-pass: ~38.13 Hz, Q ~= 0.5003270.
  const f0 = 38.13547087602444;
  const Q = 0.5003270373238773;
  const K = Math.tan((Math.PI * f0) / sr);
  const a0 = 1 + K / Q + K * K;
  return [1, -2, 1, (2 * (K * K - 1)) / a0, (1 - K / Q + K * K) / a0];
}

function biquad(
  input: Float32Array,
  b0: number,
  b1: number,
  b2: number,
  a1: number,
  a2: number,
): Float32Array {
  const out = new Float32Array(input.length);
  let x1 = 0,
    x2 = 0,
    y1 = 0,
    y2 = 0;
  for (let i = 0; i < input.length; i++) {
    const x0 = input[i];
    const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    out[i] = y0;
    x2 = x1;
    x1 = x0;
    y2 = y1;
    y1 = y0;
  }
  return out;
}
